// timezone-standardization
// eslint-disable-next-line no-restricted-imports
import { isLocalTimeZone, normalizeTimeZone } from "@chronosphereio/core";
import { sub, milliseconds, startOfMonth, endOfMonth, setMilliseconds, isValid } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
export const MINUTE_MS = 60000;
export const HOUR_MS = 60 * MINUTE_MS;
export const DAY_MS = 86400000;
export const WEEK_MS = 604800000;
export const MONTH_MS = 2629440000;
export const YEAR_MS = 31536000000;
export var PinStatus = /*#__PURE__*/ function(PinStatus) {
    PinStatus["PINNED"] = "pinned";
    PinStatus["PINNED_IN_DIFFERENT_TAB"] = "pinned_in_different_tab";
    PinStatus["UNPINNED"] = "unpinned";
    return PinStatus;
}({});
/**
 * Determine whether a given time range is relative
 */ export function isRelativeTimeRange(timeRange) {
    return timeRange.pastDuration !== undefined;
}
/**
 * Determine whether a given time range is absolute
 */ export function isAbsoluteTimeRange(timeRange) {
    return timeRange.start !== undefined && timeRange.end !== undefined;
}
/**
 * Returns an absolute time range from a RelativeTimeRange.
 */ export function toAbsoluteTimeRange(timeRange) {
    // Set milliseconds to 0 when generating a new date to avoid propagating a somewhat random
    // milliseconds value to the new date that isn't user visible in the UX (e.g. the time picker) and
    // could potentially lead to some weird edge cases.
    const end = timeRange.end ?? setMilliseconds(new Date(), 0);
    return {
        start: sub(end, parseDurationString(timeRange.pastDuration)),
        end
    };
}
export var DURATION_UNIT = /*#__PURE__*/ function(DURATION_UNIT) {
    DURATION_UNIT["MILLISECOND"] = "ms";
    DURATION_UNIT["SECOND"] = "s";
    DURATION_UNIT["MINUTE"] = "m";
    DURATION_UNIT["HOUR"] = "h";
    DURATION_UNIT["DAY"] = "d";
    DURATION_UNIT["WEEK"] = "w";
    DURATION_UNIT["MONTH"] = "M";
    DURATION_UNIT["YEAR"] = "y";
    return DURATION_UNIT;
}({});
/**
 * Regular expression pattern for parsing duration strings.
 * The pattern matches strings in the format: "1y2mo3w4d5h6m7s8ms"
 * where each component represents a duration unit (years, weeks, days, hours, minutes, seconds, milliseconds).
 * The captured groups can be used to extract the individual duration components.
 */ const DURATION_REGEX = new RegExp(`^(?:(\\d+)${"y"})?(?:(\\d+)${"M"})?(?:(\\d+)${"w"})?(?:(\\d+)${"d"})?(?:(\\d+)${"h"})?(?:(\\d+)${"m"})?(?:(\\d+)s)?(?:(\\d+)${"ms"})?$`);
/**
 * Parses a DurationString into a Duration object with numeric values that can
 * be used to do Date math. Throws if not a valid duration string.
 */ export function parseDurationString(durationString) {
    const matches = DURATION_REGEX.exec(durationString.trim());
    if (matches === null) {
        throw new Error(`Invalid duration string '${durationString}'`);
    }
    return {
        years: parseInt(matches[1] ?? '0'),
        months: parseInt(matches[2] ?? '0'),
        weeks: parseInt(matches[3] ?? '0'),
        days: parseInt(matches[4] ?? '0'),
        hours: parseInt(matches[5] ?? '0'),
        minutes: parseInt(matches[6] ?? '0'),
        seconds: parseInt(matches[7] ?? '0') + parseInt(matches[8] ?? '0') / 1000
    };
}
export function getDurationStringMilliseconds(durationString) {
    if (durationString === undefined || durationString === '') return undefined;
    const duration = parseDurationString(durationString);
    return milliseconds(duration);
}
/**
 * Converts a DurationString to seconds, rounding down.
 */ export function getDurationStringSeconds(durationString) {
    const durationMs = getDurationStringMilliseconds(durationString);
    if (typeof durationMs !== 'number') {
        return undefined;
    }
    return Math.floor(durationMs / 1000);
}
/**
 * Returns true if the given string is a valid DurationString.
 */ export function isDurationString(maybeDuration) {
    if (maybeDuration === '') return false;
    return DURATION_REGEX.test(maybeDuration);
}
/**
 * Converts a duration object into a duration string
 */ export function getDurationString(duration) {
    const result = `${duration.years ? `${duration.years}${"y"}` : ''}` + `${duration.months ? `${duration.months}${"M"}` : ''}` + `${duration.weeks ? `${duration.weeks}${"w"}` : ''}` + `${duration.days ? `${duration.days}${"d"}` : ''}` + `${duration.hours ? `${duration.hours}${"h"}` : ''}` + `${duration.minutes ? `${duration.minutes}${"m"}` : ''}` + `${duration.seconds ? `${duration.seconds}${"s"}` : ''}`;
    if (result === '') return undefined;
    return result;
}
/**
 * Used to calculate a lower, more precise step.
 * Chosen based on the screen size of a 1920x1080 monitor.
 */ export const MAX_DATA_POINTS_HIGH_RESOLUTION = 1920;
export const MIN_STEP_SIZE_MS = 15000;
const ROUNDED_STEP_INTERVALS = [
    // max: 17.5s, rounded: 15s
    {
        maxMs: 17500,
        roundedStepMs: MIN_STEP_SIZE_MS
    },
    // max: 25s, rounded: 20s
    {
        maxMs: 25000,
        roundedStepMs: 20000
    },
    // max: 45s, rounded: 30s
    {
        maxMs: 45000,
        roundedStepMs: 30000
    },
    // max: 1.5m, rounded: 1m
    {
        maxMs: 90000,
        roundedStepMs: MINUTE_MS
    },
    // max: 3.5m, rounded: 2m
    {
        maxMs: 210000,
        roundedStepMs: 120000
    },
    // max: 7.5m, rounded: 5m
    {
        maxMs: 450000,
        roundedStepMs: 300000
    },
    // max: 12.5m, rounded: 10m
    {
        maxMs: 750000,
        roundedStepMs: 600000
    },
    // max: 17.5m, rounded: 15m
    {
        maxMs: 1050000,
        roundedStepMs: 900000
    },
    // max: 25m, rounded: 20m
    {
        maxMs: 1500000,
        roundedStepMs: 1200000
    },
    // max: 45m, rounded: 30m
    {
        maxMs: 2700000,
        roundedStepMs: 1800000
    },
    // max: 1.5h, rounded: 1h
    {
        maxMs: 5400000,
        roundedStepMs: HOUR_MS
    },
    // max: 2.5h, rounded: 2h
    {
        maxMs: 9000000,
        roundedStepMs: 7200000
    },
    // max: 4.5h, rounded: 3h
    {
        maxMs: 16200000,
        roundedStepMs: 10800000
    },
    // max: 9h, rounded: 6h
    {
        maxMs: 32400000,
        roundedStepMs: 21600000
    },
    // max: 1d, rounded: 12h
    {
        maxMs: DAY_MS,
        roundedStepMs: 43200000
    },
    // max: 1w, rounded: 1d
    {
        maxMs: WEEK_MS,
        roundedStepMs: DAY_MS
    },
    // max: 3w, rounded: 1w
    {
        maxMs: 1814400000,
        roundedStepMs: WEEK_MS
    },
    // max: 6w, rounded: 30d
    {
        maxMs: 3628800000,
        roundedStepMs: 2592000000
    },
    // max: 2y, rounded: 1y
    {
        maxMs: 63072000000,
        roundedStepMs: 31536000000
    }
];
/**
 * Round interval to clearer increments
 */ export function roundStepInterval(stepMs) {
    for (const { maxMs, roundedStepMs } of ROUNDED_STEP_INTERVALS){
        if (stepMs < maxMs) {
            return roundedStepMs;
        }
    }
    return MIN_STEP_SIZE_MS;
}
/**
 * Gets a suggested step/interval size for a time range based on the max datapoints of a visual component.
 * TODO: optimize step for better performance (sc-98578)
 */ export function getSuggestedStepMs(timeRange, maxDatapoints) {
    const queryRangeMs = timeRange.end.valueOf() - timeRange.start.valueOf();
    const stepMs = Math.floor(queryRangeMs / maxDatapoints);
    return roundStepInterval(stepMs);
}
/**
 * Returns true if the given value is a valid Date object.
 */ export function isValidDate(d) {
    return isValid(d);
}
/**
 * Returns a time range representing the start and end of the specified month.
 * @param monthDate Date within the current month. If not specified, the current date will be used.
 * @param timeZone Time zone to be used when calculating the start/end of the month.
 */ export function getMonthTimeRange(monthDate = new Date(), timeZone) {
    const normalizedTimeZone = normalizeTimeZone(timeZone);
    const localTimeZone = isLocalTimeZone(normalizedTimeZone);
    if (normalizedTimeZone === 'UTC') {
        const year = monthDate.getUTCFullYear();
        const month = monthDate.getUTCMonth();
        const start = new Date(Date.UTC(year, month, 1, 0, 0, 0, 0));
        const end = new Date(Date.UTC(year, month + 1, 0, 23, 59, 59, 0));
        return {
            start,
            end: setMilliseconds(end, 0)
        };
    }
    // Ensure we are grabbing the start of the correct month for the specified time zone.
    // Otherwise, we could accidentally grab the wrong month near the edge of month
    // boundaries.
    const zonedMonthDate = localTimeZone ? monthDate : utcToZonedTime(monthDate, normalizedTimeZone);
    // Grab start/end and convert them to the correct time zone, when applicable.
    const start = localTimeZone ? startOfMonth(zonedMonthDate) : utcToZonedTime(startOfMonth(zonedMonthDate), normalizedTimeZone);
    const end = localTimeZone ? endOfMonth(zonedMonthDate) : utcToZonedTime(endOfMonth(zonedMonthDate), normalizedTimeZone);
    return {
        start,
        end: setMilliseconds(end, 0)
    };
}
// Common date and time formats for use with date-fns that should be used throughout
// viz-framework with `formatWithTimeZone` for consistency.
export var DATE_FORMAT = /*#__PURE__*/ function(DATE_FORMAT) {
    DATE_FORMAT["YEAR"] = "yyyy";
    DATE_FORMAT["SHORT"] = "MM/dd/yyyy";
    DATE_FORMAT["MEDIUM"] = "MMM dd, yyyy";
    DATE_FORMAT["MEDIUM_NO_YEAR"] = "MMM dd";
    return DATE_FORMAT;
}({});
// NOTE: supporting both 12-hour (i.e. with am/pm) and 24-hour time formats while we
// want to consistently use 24-hour time in charts (e.g. tooltip, x-axis) and are
// unsure what next steps are for the time picker, which currently uses 12-hour
// time. Ideally, this will be simplified when we align on a standard.
export var TIME_FORMAT = /*#__PURE__*/ function(TIME_FORMAT) {
    TIME_FORMAT["SHORT_12"] = "hh:mm aa";
    TIME_FORMAT["SHORT_24"] = "HH:mm";
    TIME_FORMAT["MEDIUM_12"] = "hh:mm:ss aa";
    TIME_FORMAT["MEDIUM_24"] = "HH:mm:ss";
    return TIME_FORMAT;
}({});
export const DATE_TIME_FORMAT_12 = `${"MM/dd/yyyy"} ${"hh:mm:ss aa"}`;
export const DATE_TIME_FORMAT_12_SHORT_TIME = `${"MM/dd/yyyy"} ${"hh:mm aa"}`;
/**
 * Checks whether two time ranges that can be either absolute or relative are equal.
 */ export function areTimeRangesEqual(a, b) {
    if (a === b) return true;
    if (!a || !b) return false;
    if (isRelativeTimeRange(a) && isRelativeTimeRange(b)) {
        return a.pastDuration === b.pastDuration && a.end?.getTime() === b.end?.getTime();
    }
    if (isAbsoluteTimeRange(a) && isAbsoluteTimeRange(b)) {
        const startA = a.start instanceof Date ? a.start : new Date(a.start);
        const endA = a.end instanceof Date ? a.end : new Date(a.end);
        const startB = b.start instanceof Date ? b.start : new Date(b.start);
        const endB = b.end instanceof Date ? b.end : new Date(b.end);
        return startA.getTime() === startB.getTime() && endA.getTime() === endB.getTime();
    }
    // When one time range is relative and the other is absolute, they cannot be equal
    return false;
}

//# sourceMappingURL=time.js.map